import PatternNotesGroupRowList from '@/pages/panels/details/PatternNotesGroupRowList'
import { theme } from '@/theme'
import { NoteType } from '@/utils/constants'
import { getColorSwatches, isNumeric } from '@/utils/helper'
import { Box, styled, Typography, useMediaQuery } from '@mui/material'
import { useMemo } from 'react'

interface PatternNotesFooterPanelProps {
  patternNoteGroups: any
  onCollapse: () => void
}

const FooterContainer = styled(Box)(({ theme }) => ({
  position: 'sticky',
  bottom: 0,
  width: '100vw',
  maxWidth: '100vw',
  left: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(16px)',
  borderTop: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: '0px -4px 24px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
  padding: theme.spacing(2, 2),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3, 4),
  },
  zIndex: theme.zIndex.appBar,
  overflowX: 'auto',
  overflowY: 'visible',
  '&::-webkit-scrollbar': { height: '4px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
    '&:hover': { background: 'rgba(255, 255, 255, 0.35)' },
  },
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent',
}))
const FooterContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: theme.spacing(3),

  maxWidth: '100vw',

  flexDirection: 'row',
  overflowX: 'auto',
  overflowY: 'visible',
  paddingBottom: theme.spacing(0.5),
  scrollSnapType: 'x proximity',

  '&::-webkit-scrollbar': {
    height: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.35)',
    },
  },
  scrollbarWidth: 'thin', // Firefox
  scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent',
}))

const NoteCard = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0, // ← prevent squishing
  scrollSnapAlign: 'start',
}))

export const PatternNotesFooterPanel = ({
  onCollapse: _onCollapse,
  patternNoteGroups,
}: PatternNotesFooterPanelProps) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const patternDetails = patternNoteGroups?.patternDetails
  const templateNotes = patternNoteGroups?.[NoteType.TEMPLATE] || []
  const frameNotes = patternNoteGroups?.[NoteType.FRAME] || []
  const materialNotes = patternNoteGroups?.[NoteType.MATERIAL] || []
  const accentNotes = patternNoteGroups?.[NoteType.ACCENT] || []
  const printNotes = patternNoteGroups?.[NoteType.PRINT] || []
  const colorNotes = patternNoteGroups?.[NoteType.COLOR] || []
  const borderNotes = patternNoteGroups?.[NoteType.BOARDER] || []

  const totalChildItems = useMemo(
    () =>
      templateNotes.length +
      frameNotes.length +
      materialNotes.length +
      accentNotes.length +
      printNotes.length +
      (colorNotes.length > 0 ? getColorSwatches(patternDetails).length : 0) +
      borderNotes.length,
    [patternNoteGroups],
  )

  const hasTemplate =
    templateNotes.length > 0 ||
    (patternDetails?.templateID !== undefined && patternDetails?.templateID !== null)
  // const hasFrame =
  //   (frameNotes.length > 0 && frameNotes[0]?.noteText !== 'null') ||
  //   (patternDetails?.frameID !== undefined && patternDetails?.frameID !== null)

  const computedHasFrame =
    (frameNotes.length > 0 && frameNotes[0]?.noteText !== 'null') ||
    (patternDetails?.frameID !== undefined && patternDetails?.frameID !== null)

  // 3. Assign it to your original variable
  const hasFrame = computedHasFrame
  const materialIds = Object.values(patternDetails?.materialID || {}) as Array<unknown>
  const hasMaterial =
    materialNotes.length > 0 || materialIds.some((id) => id !== undefined && id !== null)
  const accentId = patternDetails?.accentID
  const hasAccent =
    (accentNotes.length > 0 && accentNotes[0]?.noteText !== 'null') || isNumeric(accentId)
  const printSources = Object.values(patternDetails?.print || {}) as Array<{ id?: unknown }>
  const hasPrint =
    printNotes.length > 0 ||
    printSources.some((item) => item?.id !== undefined && item?.id !== null)
  const colorPalettes = Object.values(patternDetails?.color || {}) as Array<unknown>
  const hasColor =
    colorNotes.length > 0 ||
    colorPalettes.some((palette) => Array.isArray(palette) && palette.length > 0)
  const borderText = borderNotes[0]?.noteText
  let hasBorder = false
  if (borderNotes.length > 0 && borderText && borderText !== 'null') {
    try {
      const parsed = JSON.parse(borderText)
      hasBorder = parsed?.width !== undefined && parsed?.width !== 0
    } catch (e) {
      hasBorder = true
    }
  }

  return (
    <FooterContainer>
      <FooterContent
        sx={{ justifyContent: totalChildItems > 10 || isMobile ? 'flex-start' : 'center' }}
      >
        {hasTemplate && !hasAccent && (
          <NoteCard>
            <PatternNotesGroupRowList
              title={'Template'}
              patternNoteType={NoteType.TEMPLATE}
              data={templateNotes}
              patternDetails={patternDetails}
            />
          </NoteCard>
        )}

        {hasAccent && (
          <NoteCard>
            <PatternNotesGroupRowList
              title={'Template'}
              patternNoteType={NoteType.ACCENT}
              data={accentNotes}
              patternDetails={patternDetails}
            />
          </NoteCard>
        )}

        {hasFrame && (
          <NoteCard>
            <PatternNotesGroupRowList
              title={'Frame'}
              patternNoteType={NoteType.FRAME}
              data={frameNotes}
              patternDetails={patternDetails}
            />
          </NoteCard>
        )}

        {hasMaterial && (
          <NoteCard>
            <PatternNotesGroupRowList
              title={'Material'}
              patternNoteType={NoteType.MATERIAL}
              data={materialNotes}
              patternDetails={patternDetails}
            />
          </NoteCard>
        )}

        {hasPrint && (
          <NoteCard>
            <PatternNotesGroupRowList
              title={'Print'}
              patternNoteType={NoteType.PRINT}
              data={printNotes}
              patternDetails={patternDetails}
            />
          </NoteCard>
        )}

        {hasColor && (
          <NoteCard>
            <PatternNotesGroupRowList
              title={'Color Swatches'}
              patternNoteType={NoteType.COLOR}
              data={colorNotes}
              patternDetails={patternDetails}
            />
          </NoteCard>
        )}

        {hasBorder && (
          <NoteCard>
            <PatternNotesGroupRowList
              title={'Border'}
              patternNoteType={NoteType.BOARDER}
              data={borderNotes}
              patternDetails={patternDetails}
            />
          </NoteCard>
        )}
      </FooterContent>
    </FooterContainer>
  )
}
